"use client";

import { c as _c } from "react/compiler-runtime";
import React, { type JSX, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetWaveData } from "@/contexts/TitleContext";
import { useContentTab } from "../ContentTabContext";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import MyStreamWaveChat from "./MyStreamWaveChat";
import MyStreamWaveCurationContent from "./curations/MyStreamWaveCurationContent";
import { useWaveData } from "@/hooks/useWaveData";
import MyStreamWaveLeaderboard from "./MyStreamWaveLeaderboard";
import MyStreamWaveSubmissions from "./MyStreamWaveSubmissions";
import MyStreamWaveOutcome from "./MyStreamWaveOutcome";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { WaveWinners } from "@/components/waves/winners/WaveWinners";
import { MyStreamWaveTab } from "@/types/waves.types";
import { MyStreamWaveTabs } from "./tabs/MyStreamWaveTabs";
import MyStreamWaveMyVotes from "./votes/MyStreamWaveMyVotes";
import MyStreamWaveFAQ from "./MyStreamWaveFAQ";
import MyStreamWaveSales from "./MyStreamWaveSales";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { useWaveEligibility } from "@/contexts/wave/WaveEligibilityContext";
import { createBreakpoint } from "react-use";
import { getHomeRoute, getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useWaveViewMode } from "@/hooks/useWaveViewMode";
import { useWave } from "@/hooks/useWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getDropQueryKey } from "@/services/api/drop-api";
export interface MyStreamWaveProps {
  readonly waveId: string;
}
const getContentTabPanelId = (tab: MyStreamWaveTab): string => `my-stream-wave-tabpanel-${tab.toLowerCase()}`;
const useBreakpoint = createBreakpoint({
  LG: 1024,
  S: 0
});
const MyStreamWaveContent: React.FC<MyStreamWaveProps> = t0 => {
  const $ = _c(101);
  const {
    waveId
  } = t0;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const {
    isApp
  } = useDeviceInfo();
  const queryClient = useQueryClient();
  const {
    waves,
    directMessages,
    registerWave
  } = useMyStream();
  const {
    updateEligibility
  } = useWaveEligibility();
  let t1;
  if ($[0] !== isApp || $[1] !== pathname || $[2] !== router || $[3] !== searchParams) {
    t1 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      const basePath = getWaveHomeRoute({
        isDirectMessage: pathname.startsWith("/messages"),
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath || getHomeRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[0] = isApp;
    $[1] = pathname;
    $[2] = router;
    $[3] = searchParams;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1 || $[6] !== waveId) {
    t2 = {
      waveId,
      onWaveNotFound: t1
    };
    $[5] = t1;
    $[6] = waveId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    data: wave
  } = useWaveData(t2);
  let t3;
  let t4;
  if ($[8] !== registerWave || $[9] !== waveId) {
    t3 = () => {
      registerWave(waveId, true);
    };
    t4 = [registerWave, waveId];
    $[8] = registerWave;
    $[9] = waveId;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[12] !== updateEligibility || $[13] !== wave) {
    t5 = () => {
      if (!wave) {
        return;
      }
      updateEligibility(wave.id, {
        authenticated_user_eligible_to_chat: wave.chat.authenticated_user_eligible,
        authenticated_user_eligible_to_vote: wave.voting.authenticated_user_eligible,
        authenticated_user_eligible_to_participate: wave.participation.authenticated_user_eligible,
        authenticated_user_admin: wave.wave.authenticated_user_eligible_for_admin
      });
    };
    t6 = [updateEligibility, wave];
    $[12] = updateEligibility;
    $[13] = wave;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] !== directMessages.list || $[17] !== waveId || $[18] !== waves.list) {
    t7 = waves.list.find(w_0 => w_0.id === waveId) ?? directMessages.list.find(w => w.id === waveId);
    $[16] = directMessages.list;
    $[17] = waveId;
    $[18] = waves.list;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const waveFromList = t7;
  const t8 = waveFromList?.newDropsCount.count ?? 0;
  const t9 = waveFromList?.firstUnreadDropSerialNo ?? null;
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = {
      newDropsCount: t8,
      firstUnreadSerialNo: t9
    };
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const enhancedData = t10;
  const newDropsCount = enhancedData.newDropsCount;
  let t11;
  if ($[23] !== newDropsCount || $[24] !== wave) {
    t11 = wave ? {
      name: wave.name,
      newItemsCount: newDropsCount
    } : null;
    $[23] = newDropsCount;
    $[24] = wave;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  useSetWaveData(t11);
  const stableWaveKey = `wave-${waveId}`;
  const {
    activeContentTab
  } = useContentTab();
  let t12;
  if ($[26] !== searchParams) {
    t12 = searchParams.get("curation");
    $[26] = searchParams;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const activeCurationId = t12;
  const {
    viewMode,
    setViewMode,
    toggleViewMode
  } = useWaveViewMode(waveId);
  const {
    isRankWave,
    isApproveWave,
    isMemesWave,
    isDm
  } = useWave(wave);
  const showGalleryToggle = !isRankWave && !isApproveWave && !isMemesWave && !isDm;
  let t13;
  if ($[28] !== searchParams) {
    t13 = searchParams.get("serialNo");
    $[28] = searchParams;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const hasSerialTarget = t13 !== null;
  let t14;
  let t15;
  if ($[30] !== hasSerialTarget || $[31] !== setViewMode || $[32] !== showGalleryToggle || $[33] !== viewMode || $[34] !== wave) {
    t14 = () => {
      if (!wave || !hasSerialTarget || !showGalleryToggle || viewMode !== "gallery") {
        return;
      }
      setViewMode("chat");
    };
    t15 = [hasSerialTarget, setViewMode, showGalleryToggle, viewMode, wave];
    $[30] = hasSerialTarget;
    $[31] = setViewMode;
    $[32] = showGalleryToggle;
    $[33] = viewMode;
    $[34] = wave;
    $[35] = t14;
    $[36] = t15;
  } else {
    t14 = $[35];
    t15 = $[36];
  }
  useEffect(t14, t15);
  useBreakpoint();
  let t16;
  if ($[37] !== pathname || $[38] !== queryClient || $[39] !== router || $[40] !== searchParams) {
    t16 = drop => {
      queryClient.setQueryData(getDropQueryKey(drop.id), drop as ApiDrop);
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("drop", drop.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[37] = pathname;
    $[38] = queryClient;
    $[39] = router;
    $[40] = searchParams;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const onDropClick = t16;
  let t17;
  if ($[42] !== pathname || $[43] !== router || $[44] !== searchParams) {
    t17 = curationId => {
      const params_1 = new URLSearchParams(searchParams.toString() || "");
      if (curationId) {
        params_1.set("curation", curationId);
      } else {
        params_1.delete("curation");
      }
      const nextQuery = params_1.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      const currentQuery = searchParams.toString();
      const currentUrl = currentQuery ? `${pathname}?${currentQuery}` : pathname;
      if (nextUrl === currentUrl) {
        return;
      }
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[42] = pathname;
    $[43] = router;
    $[44] = searchParams;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  const onSelectCuration = t17;
  if (!wave) {
    return null;
  }
  const t18 = showGalleryToggle ? viewMode : "chat";
  let t19;
  if ($[46] !== enhancedData.firstUnreadSerialNo || $[47] !== onDropClick || $[48] !== t18 || $[49] !== wave) {
    t19 = <MyStreamWaveChat wave={wave} firstUnreadSerialNo={enhancedData.firstUnreadSerialNo} viewMode={t18} onDropClick={onDropClick} />;
    $[46] = enhancedData.firstUnreadSerialNo;
    $[47] = onDropClick;
    $[48] = t18;
    $[49] = wave;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== onDropClick || $[52] !== wave) {
    t20 = <MyStreamWaveLeaderboard key={wave.id} wave={wave} onDropClick={onDropClick} />;
    $[51] = onDropClick;
    $[52] = wave;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== onDropClick || $[55] !== wave) {
    t21 = <MyStreamWaveSubmissions wave={wave} onDropClick={onDropClick} />;
    $[54] = onDropClick;
    $[55] = wave;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== wave.id) {
    t22 = <MyStreamWaveSales waveId={wave.id} />;
    $[57] = wave.id;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== onDropClick || $[60] !== wave) {
    t23 = <WaveWinners wave={wave} onDropClick={onDropClick} />;
    $[59] = onDropClick;
    $[60] = wave;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== wave) {
    t24 = <MyStreamWaveOutcome wave={wave} />;
    $[62] = wave;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== onDropClick || $[65] !== wave) {
    t25 = <MyStreamWaveMyVotes wave={wave} onDropClick={onDropClick} />;
    $[64] = onDropClick;
    $[65] = wave;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== wave) {
    t26 = <MyStreamWaveFAQ wave={wave} />;
    $[67] = wave;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] !== t19 || $[70] !== t20 || $[71] !== t21 || $[72] !== t22 || $[73] !== t23 || $[74] !== t24 || $[75] !== t25 || $[76] !== t26) {
    t27 = {
      [MyStreamWaveTab.CHAT]: t19,
      [MyStreamWaveTab.LEADERBOARD]: t20,
      [MyStreamWaveTab.SUBMISSIONS]: t21,
      [MyStreamWaveTab.SALES]: t22,
      [MyStreamWaveTab.WINNERS]: t23,
      [MyStreamWaveTab.OUTCOME]: t24,
      [MyStreamWaveTab.MY_VOTES]: t25,
      [MyStreamWaveTab.FAQ]: t26
    };
    $[69] = t19;
    $[70] = t20;
    $[71] = t21;
    $[72] = t22;
    $[73] = t23;
    $[74] = t24;
    $[75] = t25;
    $[76] = t26;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  const components = t27;
  let t28;
  if ($[78] !== activeCurationId || $[79] !== onSelectCuration || $[80] !== showGalleryToggle || $[81] !== toggleViewMode || $[82] !== viewMode || $[83] !== wave) {
    t28 = <MyStreamWaveTabs wave={wave} viewMode={viewMode} onToggleViewMode={toggleViewMode} showGalleryToggle={showGalleryToggle} activeCurationId={activeCurationId} onSelectCuration={onSelectCuration} />;
    $[78] = activeCurationId;
    $[79] = onSelectCuration;
    $[80] = showGalleryToggle;
    $[81] = toggleViewMode;
    $[82] = viewMode;
    $[83] = wave;
    $[84] = t28;
  } else {
    t28 = $[84];
  }
  let t29;
  if ($[85] !== activeContentTab || $[86] !== activeCurationId) {
    t29 = activeCurationId ? `my-stream-wave-tabpanel-curation-${activeCurationId}` : getContentTabPanelId(activeContentTab);
    $[85] = activeContentTab;
    $[86] = activeCurationId;
    $[87] = t29;
  } else {
    t29 = $[87];
  }
  let t30;
  if ($[88] !== activeContentTab || $[89] !== activeCurationId || $[90] !== components || $[91] !== onDropClick || $[92] !== wave) {
    t30 = activeCurationId ? <MyStreamWaveCurationContent key={activeCurationId} wave={wave} curationId={activeCurationId} onDropClick={onDropClick} /> : components[activeContentTab];
    $[88] = activeContentTab;
    $[89] = activeCurationId;
    $[90] = components;
    $[91] = onDropClick;
    $[92] = wave;
    $[93] = t30;
  } else {
    t30 = $[93];
  }
  let t31;
  if ($[94] !== t29 || $[95] !== t30) {
    t31 = <div className="tw-relative tw-min-h-0 tw-min-w-0 tw-flex-grow tw-overflow-hidden" role="tabpanel" id={t29}>{t30}</div>;
    $[94] = t29;
    $[95] = t30;
    $[96] = t31;
  } else {
    t31 = $[96];
  }
  let t32;
  if ($[97] !== stableWaveKey || $[98] !== t28 || $[99] !== t31) {
    t32 = <div className="tailwind-scope tw-relative tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-col" key={stableWaveKey}>{t28}{t31}</div>;
    $[97] = stableWaveKey;
    $[98] = t28;
    $[99] = t31;
    $[100] = t32;
  } else {
    t32 = $[100];
  }
  return t32;
};
export default MyStreamWaveContent;